import { ActivityIndicator, FlatList, Pressable, ScrollView, View, useDripsyTheme } from 'dripsy';
import { t } from 'i18next';
import React, { useContext, useState } from 'react';

import { keyGenerator } from '@/utils/keyGenerator';
import { useNavigation } from '@react-navigation/native';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInput } from '@/design-system/components/inputs';
import { TextBody, TextSmall } from '@/design-system/components/typography';
import { ModalHeader } from '@/design-system/components/modal';
import { LayoutContext } from 'contexts/LayoutContext';
import { useAccounts } from '../../hooks/useCardAccounts.hook';
import { debounce } from 'lodash';

export interface SelectAccountWebScreenProps {
  onLayout: (event: any) => void;
  onSelect: (data?: any) => void;
  onChangeView?: () => void;
  borderRadiusFlag?: boolean;
}

export const SelectAccountWebScreen: React.FC<SelectAccountWebScreenProps> = (props) => {
  const { onLayout, onSelect, onChangeView, borderRadiusFlag } = props;
  const { theme } = useDripsyTheme();

  const [search, setSearch] = useState('');
  const { accounts, isLoading } = useAccounts({ pageSize: 10, search });

  const layout = useContext(LayoutContext);
  const navigation = useNavigation();
  const onBack = () => {
    navigation?.goBack();
  };

  const onSelectWallet = (data: any) => {
    onSelect(data);
    onChangeView && onChangeView();
    onBack();
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary',
        borderRadius: borderRadiusFlag ? (theme.space?.$radius2 as number) : undefined
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={{ height: 70, justifyContent: 'center' }}>
        <ModalHeader
          sx={
            borderRadiusFlag
              ? {
                  borderTopLeftRadius: theme.space?.$radius2 as number,
                  borderTopRightRadius: theme.space?.$radius2 as number,
                  overflow: 'hidden'
                }
              : undefined
          }
          title={t('earn-invest::glossary.select-account')}
          closeModal={() => {
            onChangeView && onChangeView();
            onBack();
          }}
        />
      </View>
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={{ padding: '$7', minHeight: 360 }}>
          <View sx={{ pb: '$3' }}>
            <UniInput
              prefixIcon="search"
              wrapperSx={{ flex: 1 }}
              sx={{ width: '100%' }}
              onValueChange={debounce((value) => setSearch(value || ''), 1000)}
              placeholder={t('card-account::credit-card-new-account.glossary.search-by-name')}
            />
          </View>
          <View sx={{ height: 'auto' }}>
            {!isLoading ? (
              accounts.length > 0 ? (
                <FlatList
                  data={accounts}
                  keyExtractor={(item) => keyGenerator('accountSelector')}
                  renderItem={({ item }) => (
                    <Pressable
                      sx={{
                        paddingY: '$2',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: 'transparent'
                      }}
                      onPress={() => onSelectWallet(item)}>
                      <View sx={{ pt: '$3', pb: '$1' }}>
                        <TextSmall semiBold sx={{ color: '$secondary' }}>
                          {item?.name || ' - '}
                        </TextSmall>
                        <TextSmall sx={{ color: '$textSecondary' }}>{item.accountNumber}</TextSmall>
                      </View>
                    </Pressable>
                  )}
                />
              ) : (
                <View sx={{ flex: 1, height: '100%', paddingY: '$3', justifyContent: 'center', alignItems: 'center' }}>
                  <Icon name="listZeroState" height={60} width={60} />
                  <TextBody bold sx={{ textAlign: 'center', marginTop: '$3' }}>
                    {t('earn-invest::glossary.no-accounts-found')}
                  </TextBody>
                </View>
              )
            ) : (
              <View sx={{ padding: '$5' }}>
                <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} />
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
};
